// src/api/features/tour-types/mutations.ts

import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  createTourType,
  updateTourType,
  deleteTourType,
} from "./api";

import { TourTypesKeys } from "./queries";

import type {
  TourType,
  CreateTourTypePayload,
  UpdateTourTypePayload,
} from "./types";

/* ======================================================
 * CREATE – POST /tour-types
 * ====================================================== */

export function useCreateTourTypeMutation() {
  const qc = useQueryClient();

  return useMutation<TourType, Error, CreateTourTypePayload>({
    mutationFn: (payload) => createTourType(payload),

    onSuccess(created) {
      // Cache the newly created detail
      qc.setQueryData<TourType>(
        TourTypesKeys.detail(created.id),
        created
      );

      // Refresh all lists
      qc.invalidateQueries({ queryKey: TourTypesKeys.all });
    },
  });
}

/* ======================================================
 * UPDATE – PUT /tour-types/:id
 * ====================================================== */

export function useUpdateTourTypeMutation(id: number | string) {
  const qc = useQueryClient();

  return useMutation<TourType, Error, UpdateTourTypePayload>({
    mutationFn: (payload) => updateTourType(id, payload),

    onSuccess(updated) {
      // Update detail cache
      qc.setQueryData<TourType>(
        TourTypesKeys.detail(id),
        updated
      );

      // Refresh lists
      qc.invalidateQueries({ queryKey: TourTypesKeys.all });
    },
  });
}

/* ======================================================
 * DELETE – DELETE /tour-types/:id
 * ====================================================== */

export function useDeleteTourTypeMutation() {
  const qc = useQueryClient();

  return useMutation<{ message: string } | void, Error, number | string>({
    mutationFn: (id) => deleteTourType(id),

    onSuccess(_res, id) {
      // Remove detail cache
      qc.removeQueries({ queryKey: TourTypesKeys.detail(id) });

      // Re-fetch lists
      qc.invalidateQueries({ queryKey: TourTypesKeys.all });
    },
  });
}
